/**
 * Builds the 4 core safety metrics from normalized intel data.
 * Shared between TokenIntel and WebSearch pages.
 */

import type { CoreMetric } from '../components/intel/ProCoreMetrics.js';
import type { NormalizedIntel } from '../utils/intelNormalizer.js';
import { formatSafetyScore } from './scoreFormatter.js';

/** Map a risk level string to a core-metric status */
export function riskToStatus(level: string | null | undefined): CoreMetric['status'] {
  if (!level) return 'unknown';
  const u = level.toUpperCase();
  if (u === 'LOW' || u === 'GREEN') return 'good';
  if (u === 'MEDIUM' || u === 'YELLOW') return 'caution';
  if (u === 'HIGH' || u === 'RED') return 'danger';
  return 'unknown';
}

/** Build the 4 core safety metrics from normalized intel */
export function buildCoreMetrics(intel: NormalizedIntel): CoreMetric[] {
  const metrics: CoreMetric[] = [];

  // 1. Contract Verification
  const contract = intel.contractSafety;
  if (contract) {
    const renounced = contract.isVerified !== null
      ? (contract.isVerified ? 'Verified' : 'Unverified')
      : 'Unknown';
    metrics.push({
      label: 'Contract Verification',
      value: renounced,
      status: riskToStatus(contract.contractRiskLevel),
      explanation: contract.isVerified
        ? 'The contract source code has been published and verified on a block explorer, allowing public review.'
        : 'The contract source code has not been verified on a block explorer. The code cannot be publicly reviewed.',
      tooltipKey: 'ownershipRenounced',
    });
  } else {
    metrics.push({
      label: 'Contract Verification',
      value: 'No data',
      status: 'unknown',
      explanation: 'Contract verification data was not available from our analysis providers.',
      tooltipKey: 'ownershipRenounced',
    });
  }

  // 2. Liquidity Protection
  const liq = intel.liquiditySafety;
  if (liq) {
    const locked = liq.locked;
    const lockMonths = liq.lockMonths;
    let value = locked ? 'Locked' : 'Unlocked';
    if (locked && lockMonths) value += ` (${lockMonths}mo)`;
    metrics.push({
      label: 'Liquidity Protection',
      value,
      status: riskToStatus(liq.liquidityRiskLevel),
      explanation: locked
        ? `Liquidity is locked${liq.lockPlatform ? ` via ${liq.lockPlatform}` : ''}, which prevents the deployer from suddenly withdrawing trading liquidity.`
        : 'Liquidity is not locked. The deployer could potentially withdraw trading liquidity at any time.',
      tooltipKey: 'lpLock',
    });
  } else {
    metrics.push({
      label: 'Liquidity Protection',
      value: 'No data',
      status: 'unknown',
      explanation: 'Liquidity lock data was not available from our analysis providers.',
      tooltipKey: 'lpLock',
    });
  }

  // 3. Trading Safety (buy/sell tax)
  if (contract) {
    const buyTax = contract.buyTaxPercent;
    const sellTax = contract.sellTaxPercent;
    const hasTax = buyTax !== null || sellTax !== null;
    let value = 'No data';
    let explanation = 'Tax data was not available from our analysis providers.';
    let status: CoreMetric['status'] = 'unknown';

    if (hasTax) {
      const b = buyTax ?? 0;
      const s = sellTax ?? 0;
      value = `Buy ${b}% / Sell ${s}%`;
      if (s > 10 || b > 10) {
        status = 'danger';
        explanation = `High tax rates detected. A ${s}% sell tax means you lose ${s}% of value on every sale. Extremely high taxes can prevent profitable exits.`;
      } else if (s > 5 || b > 5) {
        status = 'caution';
        explanation = `Moderate tax rates detected. Buy tax: ${b}%, Sell tax: ${s}%. These reduce your effective value on each trade.`;
      } else {
        status = 'good';
        explanation = `Low tax rates: Buy ${b}%, Sell ${s}%. These are within normal ranges for most tokens.`;
      }
    }

    metrics.push({
      label: 'Trading Safety',
      value,
      status,
      explanation,
      tooltipKey: 'sellTax',
    });
  } else {
    metrics.push({
      label: 'Trading Safety',
      value: 'No data',
      status: 'unknown',
      explanation: 'Trading tax data was not available from our analysis providers.',
      tooltipKey: 'sellTax',
    });
  }

  // 4. Holder Distribution
  const holder = intel.holderRisk;
  if (holder) {
    const count = holder.holderCount;
    const value = count ? `${count.toLocaleString()} holders` : 'Unknown count';
    metrics.push({
      label: 'Holder Distribution',
      value,
      status: riskToStatus(holder.holderRiskLevel),
      explanation: count && count > 1000
        ? `This token has ${count.toLocaleString()} holders, indicating broader distribution across wallets.`
        : count
          ? `This token has ${count.toLocaleString()} holders. Fewer holders means higher concentration risk.`
          : 'Holder count data was not available from our analysis providers.',
      tooltipKey: 'holders',
    });
  } else {
    metrics.push({
      label: 'Holder Distribution',
      value: 'No data',
      status: 'unknown',
      explanation: 'Holder distribution data was not available from our analysis providers.',
      tooltipKey: 'holders',
    });
  }

  return metrics;
}

/** Build RiskBreakdown domain array from normalized intel */
export function buildRiskDomains(intel: NormalizedIntel): Array<{ label: string; level: string | undefined; summary: string }> {
  return [
    {
      label: 'Creator Risk',
      level: intel.creatorRisk?.riskLevel,
      summary: intel.creatorRisk
        ? `Score: ${(formatSafetyScore(intel.creatorRisk.riskScore, 100) ?? 0).toFixed(1)} / 10`
        : 'No data',
    },
    {
      label: 'Liquidity Risk',
      level: intel.liquiditySafety?.liquidityRiskLevel ?? undefined,
      summary: intel.liquiditySafety
        ? (intel.liquiditySafety.locked ? 'Liquidity locked' : 'Liquidity unlocked')
        : 'No data',
    },
    {
      label: 'Holder Risk',
      level: intel.holderRisk?.holderRiskLevel ?? undefined,
      summary: intel.holderRisk
        ? `${intel.holderRisk.holderCount?.toLocaleString() ?? '?'} holders`
        : 'No data',
    },
    {
      label: 'Contract Risk',
      level: intel.contractSafety?.contractRiskLevel ?? undefined,
      summary: intel.contractSafety
        ? (intel.contractSafety.isVerified ? 'Verified contract' : 'Unverified contract')
        : 'No data',
    },
    {
      label: 'Social Trust',
      level: intel.socialTrust?.socialTrustLevel,
      summary: intel.socialTrust
        ? `Trust: ${(formatSafetyScore(intel.socialTrust.socialTrustScore, 100) ?? 0).toFixed(1)} / 10`
        : 'No data',
    },
  ];
}
